import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import messages from '../messages';
import styles from './PasswordStrength.css';
import classnames from 'classnames';

const calculateStrength = password => {
  const DIGITS_REG_EXP = new RegExp(/\d/g);
  const SPECIAL_CHARS_REG_EXP = new RegExp(/[@!#$%^&*?_~]/g);
  const scoreAdd = (condition, val) => (condition ? val : 0);

  // checks whether a password is given
  if (!password || password.trim() === '') {
    return 0;
  }

  // calculate score
  let scoreLength = 0;
  const lowerCaseChars = password !== password.toUpperCase();
  const upperCaseChars = password !== password.toLowerCase();
  const chars = upperCaseChars || lowerCaseChars;
  const digits = (password.match(DIGITS_REG_EXP) || []).length;
  const specialChars = (password.match(SPECIAL_CHARS_REG_EXP) || []).length;

  if (password.length > 21) {
    scoreLength += 60;
  } else if (password.length > 17) {
    scoreLength += 48;
  } else if (password.length > 15) {
    scoreLength += 36;
  } else if (password.length > 11) {
    scoreLength += 24;
  } else if (password.length > 7) {
    scoreLength += 12;
  } else {
    scoreLength += 6;
  }

  const scoreChars =
    scoreAdd(lowerCaseChars, 2) +
    scoreAdd(upperCaseChars, 10) +
    scoreAdd(digits > 0, 10) +
    scoreAdd(digits > 2, 10) +
    scoreAdd(specialChars > 0, 10) +
    scoreAdd(specialChars > 1, 10) +
    scoreAdd(upperCaseChars && lowerCaseChars, 4) +
    scoreAdd(chars && digits, 4) +
    scoreAdd(chars && digits && specialChars, 4);

  const score = scoreLength + scoreChars;
  return score > 100 ? 100 : Math.round(score);
};

function strengthToLevel(strength = 0) {
  if (strength < 10) return 0;
  if (strength > 30 && strength < 59) return 2;
  if (strength > 60) return 3;
  return 1;
}

const levelLabel = {
  0: '',
  1: <FormattedMessage {...messages.weak} />,
  2: <FormattedMessage {...messages.average} />,
  3: <FormattedMessage {...messages.strong} />,
};

const Strength = ({ password }) => {
  const strengthLevel = strengthToLevel(calculateStrength(password));

  return (
    <div className={styles.strength}>
      <div className={styles.bar}>
        <div
          className={classnames(
            styles[`level-${strengthLevel}`],
            styles.progress
          )}
        />
      </div>
      {strengthLevel !== 0 && (
        <div className={styles.text}>{levelLabel[strengthLevel]}</div>
      )}
    </div>
  );
};

Strength.propTypes = {
  password: PropTypes.string,
};

export default Strength;
